import { useCallback, useEffect, useRef } from 'react';

import { useRouter } from '../hooks';
import { rootPaths } from './paths';
import { SessionManager } from '../utils/sessionManager';
import LocalStorage from '../utils/LocalStorage';

export default function AuthGuard(props: any) {
  const router = useRouter();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasRedirectedRef = useRef(false);
  const hasCheckedRef = useRef(false);

  const check = useCallback(() => {
    if (hasRedirectedRef.current) {
      return false;
    }

    const currentPath = window.location.pathname;
    if (currentPath === rootPaths.tutorials) {
      return true;
    }

    // Tokens live in sessionStorage after security fix — do not read localStorage only.
    const accessToken = LocalStorage.getApiKey();

    if (!accessToken) {
      hasRedirectedRef.current = true;
      router.replace(rootPaths.login);
      return false;
    }

    if (SessionManager.isSessionExpired()) {
      hasRedirectedRef.current = true;
      LocalStorage.logout();
      router.replace(rootPaths.login);
      return false;
    }

    return true;
  }, [router]);

  useEffect(() => {
    if (hasCheckedRef.current) {
      return;
    }
    hasCheckedRef.current = true;

    const timeoutId = setTimeout(() => {
      const isValid = check();

      if (isValid) {
        intervalRef.current = setInterval(() => {
          if (!hasRedirectedRef.current && SessionManager.isSessionExpired()) {
            hasRedirectedRef.current = true;
            LocalStorage.logout();
            router.replace(rootPaths.login);
          }
        }, 60000);
      }
    }, 200);

    return () => {
      clearTimeout(timeoutId);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    // Intentionally run once on mount. `check` is stable enough for the delayed call.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return props.children;
}
